'use client';

import React, { SyntheticEvent, useCallback, useState } from 'react';
import Icon from '@/components/shared/Icons';
import { getAvailabilityOutput } from '@/utils/ibk/getIbkAvailabilityOutput';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';
import dynamic from 'next/dynamic';
import useIbkAvailability from '@/hooks/useIbkAvailability';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import {
  INSTANT_BOOK_WIDGET_TRIGGER_CLICK_TYPE,
  INSTANT_BOOK_FROM_WIDGET_CLICK_TYPE
} from '@/utils/analyticsClientHelper';
import useMap from '@/hooks/useMap';
import { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import useAnalytics from '@/hooks/useAnalytics';
import { getFilterSelections } from '@/utils/ibk/getFilterSelections';
import useLogger from '@/hooks/useLogger';
import { VenueProvider } from '@/models/VenueProvider';
import { IBK_PROVIDER } from '@/constants/ibkConstants';

const CheckAvailability = dynamic(() => import('@/components/card/ibk/CheckAvailability'), { ssr: false });
const Tooltip = dynamic(() => import('@/components/shared/tooltip/Tooltip'), { ssr: false });

type MeetingRooms = {
  averageCostPerDayBeforeTax:
    | {
        amount: number;
      }
    | number;
  totalCostBeforeTax:
    | {
        amount: number;
      }
    | number;
  currency: string;
};

type SleepingRooms = {
  lowestAverageRateWithoutTax:
    | {
        amount: number;
      }
    | number;
  lowestAverageRateWithTax:
    | {
        amount: number;
      }
    | number;
  currency: string;
};

export type InstantBookableVenue = {
  meetingSpaceIntegration: boolean;
  sleepingSpaceIntegration: boolean;
  meetingRooms?: MeetingRooms[];
  sleepingRooms?: SleepingRooms[];
};

export type IbkStrings = {
  _csn_search_srp_ibk_banner_instant_book_text: string;
  csn_ibk_booking_banner_meetingRoom_SingleDayOnly: string;
  csn_ibk_booking_banner_meetingRoom_SingleDayOnly_tooltip: string;
  csn_ibk_booking_notAvailable: string;
  csn_ibk_only_meetingRooms: string;
  csn_ibk_only_guestRooms: string;
  csn_ibk_guestRooms_not_bookable: string;
  csn_ibk_booking_meetingRoomRate: string;
  csn_ibk_booking_guestRoomRate: string;
  csn_ibk_meetingRooms_not_bookable: string;
  csn_ibk_guestRooms_not_available: string;
  csn_ibk_meetingRooms_not_available: string;
  _venue_profile_section_ibkWidget_rfpWarning_header: string;
  _venue_profile_section_ibkWidget_rfpWarning_secondaryButton: string;
  _venue_profile_section_ibkWidget_rfpWarning_primaryButton: string;
  _venue_profile_section_ibkWidget_rfpWarning_body: string;
  csn_search_availability_modal_venueAvailable: string;
  csn_search_availability_modal_meetingRoomsOnly_Available: string;
  csn_search_availability_modal_guestRoomsOnly_Available: string;
  csn_search_availability_modal_inventoryNotAvailable: string;
  csn_search_availability_modal_numOfPeople: string;
  csn_search_availability_modal_startDate: string;
  csn_search_availability_modal_endDate: string;
  csn_search_availability_modal_roomsPerNight: string;
  csn_search_availability_modal_checkIn: string;
  csn_search_availability_modal_checkOut: string;
  csn_search_availability_modal_header: string;
  csn_search_availability_modal_selectRoomsButton: string;
  csn_search_availability_modal_checkAvailabilityButton: string;
  csn_search_availability_modal_meetingRoomTitle: string;
  csn_search_availability_modal_guestRoomTitle: string;
  _close: string;
  csn_search_guest_rooms_date_range_label: string;
  csn_search_date_range_dialog: string;
  csn_search_date_range_previous_month: string;
  csn_search_date_range_next_month: string;
  csn_search_guest_rooms_date_range_start_dialog: string;
  csn_search_guest_rooms_date_range_end_dialog: string;
  csn_ibk_booking_banner_check_availability_button_label: string;
  csn_ibk_booking_banner_book_now_button_label: string;
  vpp_ibk_guest_rooms_not_available: string;
  vpp_ibk_meeting_rooms_not_available: string;
  vpp_ibk_guest_rooms_not_instantly_bookable: string;
  vpp_ibk_meeting_rooms_not_instantly_bookable: string;
  vpp_ibk_description: string;
  vpp_ibk_heading: string;
  vpp_ibk_guest_room: string;
  vpp_ibk_meeting_room: string;
  vpp_ibk_per_day: string;
  vpp_ibk_per_night: string;
  vpp_ibk_event_dates_not_matching: string;
  csn_search_availability_modal_checkAvailabilityCalendar_prompt_calendarImage_altMessage: string;
  csn_search_availability_modal_checkAvailabilityCalendar_prompt_openCalendarButton: string;
  csn_search_availability_modal_checkAvailabilityCalendar_prompt_messageHeader: string;
  csn_search_availability_modal_checkAvailabilityCalendar_prompt_messageBody: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_header: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_headerNote: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_headerNoteMessage: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_header_message: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_month_left_arrow: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_month_right_arrow: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_clearDatesButton: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_nextButton: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_meetingRooms: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_guestRooms: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_meetingRooms_eventStartDate: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_meetingRooms_eventEndDate: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_guestRooms_checkIn: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_guestRooms_checkout: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_meetingRooms_dateRange: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_footer_guestRooms_dateRange: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_rateTiles_checkoutOnly: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_meetingRoom_rateTiles_aria_label: string;
  csn_search_availability_modal_checkAvailabilityCalendar_modal_guestRoom_rateTiles_aria_label: string;
  csn_search_availability_modal_checkAvailabilityCalendar_error_header: string;
  csn_search_availability_modal_checkAvailabilityCalendar_error_message: string;
  csn_search_availability_modal_checkAvailabilityCalendar_error_button_try_new_dates: string;
  csn_search_availability_modal_checkAvailabilityCalendar_error_button_refresh_page: string;
  _back: string;
  january: string;
  february: string;
  march: string;
  april: string;
  may: string;
  june: string;
  july: string;
  august: string;
  september: string;
  october: string;
  november: string;
  december: string;
  _sunday: string;
  _monday: string;
  _tuesday: string;
  _wednesday: string;
  _thursday: string;
  _friday: string;
  _saturday: string;
  vpp_ibk_mobile_text: string;
  vpp_ibk_mobile_booking_options: string;
  vpp_ibk_mobile_check_availability: string;
};

type IBKBannerProps = {
  venueId: string;
  venueIndex: number;
  venueName: string;
  instantBookableVenue: InstantBookableVenue;
  strings: IbkStrings;
  venueFastResponder: boolean;
  locale: Locale;
  cventGuestDomain: string;
  rfpName: string;
  searchResultsRank: number;
  searchTerm: string;
  activeFilters: string;
  diamondLevel: string | undefined;
  isIbkAvailabilityCalendarEnabled: boolean;
  providers: VenueProvider[];
  meetingSpaceEnabled?: boolean;
  sleepingSpaceEnabled?: boolean;
};

export type BannerContent = {
  primaryRoomAvailability: string;
  secondaryRoomAvailability?: string;
  isBookNowAvailable?: boolean;
};

export default function IbkBanner({
  venueId,
  venueName,
  venueIndex,
  instantBookableVenue,
  strings,
  venueFastResponder,
  locale,
  cventGuestDomain,
  rfpName,
  searchResultsRank,
  searchTerm,
  activeFilters,
  diamondLevel,
  isIbkAvailabilityCalendarEnabled,
  providers,
  meetingSpaceEnabled,
  sleepingSpaceEnabled
}: IBKBannerProps): JSX.Element {
  const { IBK_DATA } = useIbkAvailability();
  const logger = useLogger('IbkBanner');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAvailabilityCalendarModalOpen, setIsAvailabilityCalendarModalOpen] = useState(false);
  const { preFilledFilters, configSkuJWT } = IBK_DATA;
  const {
    preFilledMeetingSpaceAttendees,
    preFilledStartDate,
    preFilledEndDate,
    preFilledGuestRoomCount,
    preFilledGuestRoomStartDate,
    preFilledGuestRoomEndDate
  } = preFilledFilters;
  const eventDatesAvailable = !!(preFilledStartDate && preFilledEndDate);
  const guestRoomDatesAvailable = !!(preFilledGuestRoomStartDate && preFilledGuestRoomEndDate);
  const averageCostPerDayBeforeTax = instantBookableVenue.meetingRooms?.[0]?.averageCostPerDayBeforeTax;

  // after DW4 upgrade of csn-search-app-service, it will return averageCostPerDayBeforeTax as a number
  const meetingRoomCost =
    typeof averageCostPerDayBeforeTax === 'object' ? averageCostPerDayBeforeTax?.amount : averageCostPerDayBeforeTax;

  const { mapView } = useMap();
  const analytics = useAnalytics();

  const lowestAverageRateWithoutTax = instantBookableVenue.sleepingRooms?.[0]?.lowestAverageRateWithoutTax;
  const lowestAverageRateWithTax = instantBookableVenue.sleepingRooms?.[0]?.lowestAverageRateWithTax;

  const withoutTaxAmount =
    typeof lowestAverageRateWithoutTax === 'object' ? lowestAverageRateWithoutTax?.amount : lowestAverageRateWithoutTax;
  const withTaxAmount =
    typeof lowestAverageRateWithTax === 'object' ? lowestAverageRateWithTax?.amount : lowestAverageRateWithTax;

  const guestRoomCost = withoutTaxAmount ?? withTaxAmount;

  const isMeetingPackageIntegrated = providers.find(
    provider => provider.providerTypeCode === IBK_PROVIDER.MEETINGPACKAGE
  );
  const showSingleDayBookingOnly =
    isMeetingPackageIntegrated && preFilledStartDate?.getTime() !== preFilledEndDate?.getTime();
  const ibkRatesAvailable = !!(meetingRoomCost || guestRoomCost);

  const disabledModeOn =
    (eventDatesAvailable || guestRoomDatesAvailable) && !showSingleDayBookingOnly && !ibkRatesAvailable;

  const separator = ' • ';
  const triggerSimpleClickFact = useCallback(
    async (e, factType: string, stopPropagation = true) => {
      if (stopPropagation) {
        e.stopPropagation();
      }
      const simpleClickFact = getSimpleClickFact(
        venueId,
        factType,
        searchResultsRank,
        venueId,
        mapView,
        searchTerm,
        diamondLevel,
        venueFastResponder ? 'Yes' : 'No',
        activeFilters
      );
      analytics.reportFact<SimpleClickFact>(simpleClickFact);
    },
    [venueId, searchResultsRank, mapView, searchTerm, diamondLevel, activeFilters, analytics, venueFastResponder]
  );

  const bannerHandler = useCallback(
    async (event: SyntheticEvent, isBookNowAvailable?: boolean) => {
      event.preventDefault();
      event.stopPropagation();
      if (isBookNowAvailable) {
        triggerSimpleClickFact(event, INSTANT_BOOK_FROM_WIDGET_CLICK_TYPE);
        const filterSelections = getFilterSelections(
          !!(preFilledStartDate && preFilledEndDate),
          !!(preFilledGuestRoomStartDate && preFilledGuestRoomEndDate),
          meetingRoomCost,
          guestRoomCost,
          preFilledStartDate,
          preFilledEndDate,
          preFilledGuestRoomStartDate,
          preFilledGuestRoomEndDate,
          preFilledMeetingSpaceAttendees,
          preFilledGuestRoomCount
        );
        const ibkAppWindow = window.open();
        try {
          const response = await fetch('/venues/api/filter', {
            method: 'POST',
            body: JSON.stringify(filterSelections)
          });

          if (response.ok) {
            const res = await response.json();
            const ibkShopAndBookUrl = `${cventGuestDomain}/instantbook/venue/${venueId}?selections=${
              res.encoded ?? ''
            }${configSkuJWT !== '' ? `&configSkuJWT=${configSkuJWT}` : ''}`;
            if (ibkAppWindow) {
              ibkAppWindow.location.href = ibkShopAndBookUrl;
            }
          }
        } catch (e) {
          logger.error('Failed to redirect to IBK from venue card. Error: %o', {
            error: {
              message: e.message,
              stack: e.stack
            }
          });
        }
      } else {
        triggerSimpleClickFact(event, INSTANT_BOOK_WIDGET_TRIGGER_CLICK_TYPE);
        setIsModalOpen(!isModalOpen);
      }
    },
    [
      configSkuJWT,
      cventGuestDomain,
      guestRoomCost,
      isModalOpen,
      logger,
      meetingRoomCost,
      preFilledEndDate,
      preFilledGuestRoomCount,
      preFilledGuestRoomEndDate,
      preFilledGuestRoomStartDate,
      preFilledMeetingSpaceAttendees,
      preFilledStartDate,
      triggerSimpleClickFact,
      venueId
    ]
  );
  let currencyCode: string | undefined;
  if (meetingRoomCost || guestRoomCost) {
    if (instantBookableVenue.meetingRooms) {
      currencyCode = instantBookableVenue.meetingRooms[0]?.currency;
    } else {
      currencyCode = instantBookableVenue.sleepingRooms?.[0]?.currency;
    }
  }

  const instantBookableVenueProps = {
    isMeetingRoomIntegrated: instantBookableVenue.meetingSpaceIntegration,
    isSleepingRoomIntegrated: instantBookableVenue.sleepingSpaceIntegration,
    meetingRoomCost,
    guestRoomCost
  };

  const availabilityProps = {
    meetingRoomCost,
    guestRoomCost,
    currencyCode,
    eventDatesAvailable,
    guestRoomDatesAvailable,
    preFilledMeetingSpaceAttendees,
    preFilledGuestRoomCount,
    strings,
    locale,
    meetingSpaceEnabled,
    sleepingSpaceEnabled
  };
  const bannerContent: BannerContent = disabledModeOn
    ? { primaryRoomAvailability: formatMessage(strings.csn_ibk_booking_notAvailable, locale) }
    : getAvailabilityOutput(availabilityProps);

  const { primaryRoomAvailability, secondaryRoomAvailability, isBookNowAvailable } = bannerContent;

  const renderContent = () => {
    if (isBookNowAvailable) {
      return (
        <div data-cvent-id={`ibk-banner-button-wrapper-book-now-card-index-${venueIndex}`}>
          <b>{strings.csn_ibk_booking_banner_book_now_button_label}</b>
        </div>
      );
    }
    // show single day booking only message if meeting room is not available for multiple days
    // which is the case when venue is integrated with MeetingPackage and user searched for multiple days
    if (showSingleDayBookingOnly) {
      return (
        <div className="flex items-center text-neutral-90">
          {strings.csn_ibk_booking_banner_meetingRoom_SingleDayOnly}
          <div className="pl-2">
            <Tooltip
              text={strings.csn_ibk_booking_banner_meetingRoom_SingleDayOnly_tooltip}
              trigger={
                <span>
                  <Icon id="info" className="fill-neutral-0 h-3.5 w-3.5" />
                </span>
              }
            />
          </div>
        </div>
      );
    }

    return (
      // don't want wrapper div to be accessible to screen reader
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div>
        <CheckAvailability
          locale={locale}
          disabledModeOn={disabledModeOn}
          strings={strings}
          cventGuestDomain={cventGuestDomain}
          instantBookableVenueProps={instantBookableVenueProps}
          venueId={venueId}
          venueName={venueName}
          venueFastResponder={venueFastResponder}
          rfpName={rfpName}
          searchResultsRank={searchResultsRank}
          searchTerm={searchTerm}
          activeFilters={activeFilters}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onProfilePage={false}
          isIbkAvailabilityCalendarEnabled={isIbkAvailabilityCalendarEnabled}
          diamondLevel={diamondLevel}
          testID={`ibk-banner-button-wrapper-check-availability-card-index-${venueIndex}`}
          isAvailabilityCalendarModalOpen={isAvailabilityCalendarModalOpen}
          setIsAvailabilityCalendarModalOpen={setIsAvailabilityCalendarModalOpen}
          singleDayMeetingRoomBookingOnly={!!isMeetingPackageIntegrated}
          currencyCode={currencyCode}
        />
      </div>
    );
  };

  return (
    <button
      type="button"
      data-cvent-id={`ibk-banner-wrapper-card-index-${venueIndex}`}
      className={`rounded-b-xl hover:rounded-b-xl ${
        disabledModeOn ? 'bg-neutral-20' : 'bg-tertiary-20'
      } px-4 py-2 lg:flex lg:justify-between`}
      onClick={e => bannerHandler(e, isBookNowAvailable)}
      tabIndex={0}
    >
      <div className="flex">
        <Icon id="bolt" className={`h-5 w-5 ${disabledModeOn ? 'fill-neutral-70' : 'fill-tertiary-70'}`} />
        <div
          className={`flex flex-col items-start gap-0.5 text-b-sm lg:flex-row lg:gap-1 ${
            disabledModeOn ? 'text-neutral-70' : 'text-tertiary-70'
          }`}
          data-cvent-id="ibk-banner-text"
        >
          <b>{strings._csn_search_srp_ibk_banner_instant_book_text}</b>
          <div
            className={` ${disabledModeOn ? 'text-neutral-70' : 'text-tertiary-70'}`}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: primaryRoomAvailability }}
          />
          {primaryRoomAvailability && secondaryRoomAvailability ? (
            <div className={`hidden lg:flex ${disabledModeOn ? 'text-neutral-70' : 'text-tertiary-70'}`}>
              {separator}
            </div>
          ) : null}
          {secondaryRoomAvailability ? (
            <div
              className={`${disabledModeOn ? 'text-neutral-70' : 'text-tertiary-70'}`}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: secondaryRoomAvailability }}
            />
          ) : null}
        </div>
      </div>
      <div className="flex justify-end">
        {!showSingleDayBookingOnly && (
          <Icon id="bolt" className={`h-5 w-5 ${disabledModeOn ? 'fill-neutral-70' : 'fill-interactive-70'}`} />
        )}
        <span className={`text-b-sm ${disabledModeOn ? 'text-neutral-70' : 'text-interactive-70'}`}>
          {renderContent()}
        </span>
      </div>
    </button>
  );
}
